/**
 * Frame -> HH:MM:SS:FF.
 *
 * Non-integer rates (23.976, 29.97) are handled by rounding the nominal rate for the frame
 * field, which is what a review tool wants: the number in the corner must match the frame
 * you can step to. Drop-frame timecode is deliberately not implemented — it would show a
 * different number than the frame counter and confuse notes rather than help them.
 */
export function formatTimecode(frame: number, fps: number): string {
  const rate = Math.max(1, Math.round(fps));
  const safeFrame = Math.max(0, Math.floor(frame));

  const frames = safeFrame % rate;
  const totalSeconds = Math.floor(safeFrame / rate);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);

  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}:${pad(frames)}`;
}

/**
 * Compact seconds:frames readout, e.g. "7:12" (7 seconds, frame 12). No minutes or hours —
 * review clips are short and the number people talk about is "seconds and frames".
 */
export function formatShortTimecode(frame: number, fps: number): string {
  const rate = Math.max(1, Math.round(fps));
  const safeFrame = Math.max(0, Math.floor(frame));
  const frames = safeFrame % rate;
  const seconds = Math.floor(safeFrame / rate);
  // Both fields padded so the readout keeps a fixed width and the UI never shifts.
  return `${String(seconds).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
}

/**
 * A QuickTime start timecode ("HH:MM:SS:FF", or ";FF" for drop-frame) back to a frame number,
 * so a clip Nuke stamped as starting on frame 1001 reads 1001 in the counter instead of 0. The
 * frame field is counted against the rounded rate, matching formatTimecode; drop-frame is
 * treated as non-drop on purpose, for the same reason formatTimecode avoids it. Returns 0 when
 * there is no usable timecode, which leaves every readout 0-based exactly as before.
 *
 * The hours field must be 0: a *start-frame* timecode is always under an hour (even frame 50000
 * at 24 fps is only 00:34:43:08), whereas a full clock timecode — the plate's time-of-day code
 * that Nuke passes straight through, e.g. 20:04:07:11 — has hours > 0. Reading that as a start
 * frame is where the "frame 1733939" nonsense came from; such a code is ignored (returns 0) and
 * the counter stays 0-based until the clip carries a real frame-encoded start timecode.
 */
export function parseTimecodeToFrames(timecode: string | null | undefined, fps: number): number {
  if (!timecode) return 0;
  const m = /^(\d{1,2}):(\d{1,2}):(\d{1,2})[:;](\d{1,3})$/.exec(timecode.trim());
  if (!m) return 0;
  // Hours > 0 means a time-of-day code, not a start frame — see the note above.
  if (Number(m[1]) > 0) return 0;
  const rate = Math.max(1, Math.round(fps));
  const frames = ((Number(m[1]) * 60 + Number(m[2])) * 60 + Number(m[3])) * rate + Number(m[4]);
  return Number.isFinite(frames) && frames > 0 ? frames : 0;
}

/** Human duration for clip lists: "5.0 s" or "1:23". */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '–';
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
